/**
 * Web Audio resume-on-foreground.
 *
 * THE BUG (device-only, reported as "sounds never come back until I restart"):
 *
 * Howler keeps ONE global AudioContext. When the OS backgrounds the app, it
 * suspends that context — `Howler.ctx.state` becomes 'suspended'. Howler 2.2.3
 * registers no `statechange` listener (verified: there is no `onstatechange` in
 * its dist), and it only sets its OWN `Howler.state` to 'suspended' inside the
 * `_autoSuspend()` 30-second idle timer. An OS-driven suspend therefore leaves
 * Howler's bookkeeping stale: ctx says 'suspended', Howler still says 'running'.
 *
 * That stale flag is what makes the silence permanent, via two paths:
 *
 *  1. `Howl.play()` gates on `Howler.state === 'running' && ctx.state !== 'interrupted'`
 *     (howler.js:886). With the stale flag that test PASSES, so it starts a
 *     buffer source on a suspended context — no sound, no error, no retry.
 *  2. `Howler._autoResume()` only calls `ctx.resume()` when its own state reads
 *     'suspended' (or ctx is iOS-'interrupted'). With the stale 'running' flag
 *     it takes the first branch (howler.js:521) and merely clears a timer.
 *
 * So nothing ever resumes the context, and every later play is silently dropped.
 * Restarting the app builds a fresh context, which is why that "fixes" it.
 *
 * THE FIX: on foreground, if the context is not running, correct Howler's stale
 * flag and then let Howler's own `_autoResume()` do the work — it calls
 * `ctx.resume()` AND emits 'resume' to every Howl, which releases the sounds
 * that `play()` parked behind `_playLock` while the context was down. Doing it
 * through Howler rather than calling `ctx.resume()` ourselves is what unsticks
 * those queued sounds.
 *
 * `state`/`_autoResume` are Howler internals; howler exposes no public API for
 * this, and reading its dist is the only way to get the sequencing right. The
 * shape is asserted in utils/__tests__/audioResume.test.ts so a howler upgrade
 * that changes it fails a test rather than silently going quiet on device.
 *
 * THE RECURRENCE (EN-10967, after the fix above shipped as EN-10460):
 *
 * WebKit can bring a backgrounded context back in a state no flag correction
 * reaches. It either reports 'running' while rendering nothing (WebKit bug
 * 243727), or stays 'interrupted' and does not answer a bare resume() (WebKit
 * bug 263627). Both still need an app restart. The first is invisible to
 * {@link resumeAudioContext}, which treats 'running' as healthy and returns.
 * The workaround in those reports is a full suspend() → resume() cycle, which
 * restarts WebKit's audio unit. {@link restartAudioContext} does that once per
 * return from the background, and again on the first tap afterwards in case
 * the foreground attempt was refused outside a gesture.
 */

import { Capacitor } from '@capacitor/core';

/** The slice of Howler's global singleton this module touches. */
export type HowlerLike = {
  /** Howler's idle-suspend switch — see {@link disableIdleAudioSuspend}. */
  autoSuspend?: boolean;
  ctx?: {
    state?: string;
    resume?: () => Promise<void>;
    suspend?: () => Promise<void>;
  } | null;
  state?: string;
  usingWebAudio?: boolean;
  _autoResume?: () => void;
  _resumeAfterSuspend?: boolean;
  _howls?: { _emit: (event: string) => void }[];
};

export type ResumeOutcome =
  /** No Web Audio context to manage (HTML5-audio fallback, or not created yet). */
  | 'unavailable'
  /** Context already running — nothing to do. */
  | 'running'
  /** Context was suspended/interrupted; a resume was requested. */
  | 'resumed'
  /** Context was cycled through suspend → resume regardless of its state. */
  | 'restarted'
  | 'restarting'
  | 'failed';

const restarts = new WeakMap<HowlerLike, Promise<ResumeOutcome>>();
const needsGestureRecovery = new WeakSet<HowlerLike>();
export const AUDIO_RESTART_TIMEOUT_MS = 5000;

/**
 * Bring the shared AudioContext back after the app returns to the foreground.
 *
 * Safe to call as often as you like: it is a no-op while the context is already
 * running, so wiring it to several lifecycle signals costs nothing.
 */
export const resumeAudioContext = (howler: HowlerLike | null | undefined): ResumeOutcome => {
  const ctx = howler?.ctx;
  // `usingWebAudio: false` means howler fell back to HTML5 Audio, which has no
  // context to suspend — nothing here applies.
  if (!howler || !ctx || howler.usingWebAudio === false) return 'unavailable';
  if (restarts.has(howler)) return 'restarting';

  if (ctx.state === 'running') {
    // Keep Howler's flag honest even on the happy path: if it drifted while we
    // were away, `play()`'s gate would misread it on the next cue.
    howler.state = 'running';
    return 'running';
  }

  // ctx.state is 'suspended' (Android/Chrome background) or 'interrupted' (iOS
  // audio interruption). Correcting the flag first is what lets Howler's own
  // _autoResume take its resume branch instead of the no-op one.
  howler.state = 'suspended';

  if (typeof howler._autoResume === 'function') {
    howler._autoResume();
    return 'resumed';
  }

  // Defensive fallback if a howler upgrade drops the internal: resume directly.
  // Sounds parked behind _playLock stay parked, but new cues will be audible.
  ctx.resume?.();
  return 'resumed';
};

/**
 * Cycle the shared AudioContext through suspend → resume, whatever it reports.
 *
 * For the return from the background only: it is the one moment WebKit can hand
 * back a context whose state is lying (see THE RECURRENCE above). Both calls
 * are issued synchronously, so when this runs inside a tap the resume is still
 * inside that gesture. Completion is reported only after both settle and the
 * context actually reports running; failures remain retryable. A valid gesture
 * may replace a pending foreground cycle; older completions cannot change the
 * latest cycle's state or release its queued cues.
 *
 * Howler's flag is set to 'suspending' first so cues arriving mid-cycle park
 * behind `_playLock`. On success we emit the same resume event as _autoResume
 * to release those queued cues without another native resume request.
 */
export const restartAudioContext = (
  howler: HowlerLike | null | undefined,
  { replacePending = false }: { replacePending?: boolean } = {}
): Promise<ResumeOutcome> => {
  const ctx = howler?.ctx;
  if (!howler || !ctx || howler.usingWebAudio === false || ctx.state === 'closed') {
    return Promise.resolve('unavailable');
  }
  const pending = restarts.get(howler);
  if (pending && !replacePending) return pending;
  // Without suspend() there is nothing to cycle — the plain resume is all we have.
  if (typeof ctx.suspend !== 'function') return Promise.resolve(resumeAudioContext(howler));
  if (typeof ctx.resume !== 'function') return Promise.resolve('unavailable');

  // Howl.play() calls _autoResume itself. 'suspending' keeps that path from
  // issuing another resume or opening the play gate before this cycle settles.
  howler.state = 'suspending';
  let suspended: Promise<boolean>;
  try {
    suspended = Promise.resolve(ctx.suspend()).then(
      () => true,
      () => false
    );
  } catch {
    suspended = Promise.resolve(false);
  }
  let resumed: Promise<void>;
  try {
    // Queue both native operations inside the gesture; awaiting suspend first
    // would lose WebKit's user activation.
    resumed = Promise.resolve(ctx.resume());
  } catch (error) {
    resumed = Promise.reject(error);
  }
  // Native promises can remain pending through an interruption. Expiry releases
  // this generation's gate and lets the lifecycle owner re-arm the next tap.
  let timer: ReturnType<typeof setTimeout>;
  const expired = new Promise<ResumeOutcome>((resolve) => {
    timer = setTimeout(() => resolve('failed'), AUDIO_RESTART_TIMEOUT_MS);
  });
  const nativeOutcome = Promise.all([
    suspended,
    resumed.then(
      () => true,
      () => false
    )
  ]).then(
    ([didSuspend, didResume]): ResumeOutcome =>
      didSuspend && didResume && howler.ctx === ctx && ctx.state === 'running'
        ? 'restarted'
        : 'failed'
  );
  const operation = Promise.race([nativeOutcome, expired])
    .then((outcome): ResumeOutcome => {
      if (outcome !== 'restarted') return outcome;
      // A gesture may have queued a newer cycle while this one was pending.
      // Only that latest cycle owns the play gate and resume notification.
      if (restarts.get(howler) !== operation) return 'restarted';
      if (howler.ctx !== ctx || ctx.state !== 'running') return 'failed';
      howler.state = 'running';
      // Mirror _autoResume's successful completion. Calling _autoResume here
      // would issue an extra, unobserved resume and could reject outside a tap.
      howler._howls?.forEach((howl) => howl._emit('resume'));
      return 'restarted';
    })
    .catch((): ResumeOutcome => 'failed')
    .then((outcome) => {
      clearTimeout(timer);
      if (restarts.get(howler) !== operation) return outcome;
      howler._resumeAfterSuspend = false;
      if (outcome === 'failed') {
        howler.state = 'suspended';
        needsGestureRecovery.add(howler);
      } else {
        needsGestureRecovery.delete(howler);
      }
      restarts.delete(howler);
      return outcome;
    });
  restarts.set(howler, operation);
  // Invalidating a generation cannot cancel a native suspend. If it completes
  // after a newer recovery, repair the actual context/Howler mismatch rather
  // than leaving future Howl.play calls on a falsely running gate.
  void suspended.then((didSuspend) => {
    if (
      didSuspend &&
      !restarts.has(howler) &&
      howler.ctx === ctx &&
      ctx.state !== 'running' &&
      ctx.state !== 'closed' &&
      howler.state === 'running'
    ) {
      // The repair owns a new bounded generation too. An untracked Howler
      // _autoResume could otherwise overwrite a still-newer gesture's gate.
      void restartAudioContext(howler);
    }
  });
  return operation;
};

/**
 * Wire {@link resumeAudioContext} to every "we're back" signal and return a
 * teardown. Mirrors the Capacitor + visibilitychange pairing already used by
 * ConnectionManager and Lobby.
 *
 * Why this many signals: `visibilitychange` is unreliable inside a Capacitor
 * WebView, `appStateChange` only exists on native, and some platforms refuse
 * `resume()` outside a user gesture — so a pointer/touch fallback catches the
 * case where the foreground resume was rejected.
 */
/**
 * Turn off Howler's idle auto-suspend, so a cue never waits on a resume.
 *
 * Howler suspends the shared AudioContext after 30 seconds with nothing playing
 * (`_autoSuspend`, howler.js:505 — the timer is rescheduled every time a sound
 * ends). The next `play()` then takes the slow branch:
 *
 *     if (Howler.state === 'running' && ctx.state !== 'interrupted') playWebAudio();
 *     else { self._playLock = true; self.once('resume', playWebAudio); }
 *
 * i.e. it parks the cue until an async `ctx.resume()` round-trips. On a table
 * that has been quiet — players thinking through a slow hand — that is exactly
 * the "sound arrives late / out of sync with the animation" symptom, and it is
 * intermittent because it only happens once 30s of silence has elapsed.
 *
 * The showdown flip is the most exposed cue: it is rare, and it lands right
 * after the quietest part of a hand.
 *
 * Cost of leaving the context running is a small amount of idle CPU, which is
 * the right trade for a game client whose audio must be prompt. This does NOT
 * replace {@link installAudioResumeHandlers}: the OS still suspends the context
 * when the app is backgrounded, regardless of this flag.
 */
export const disableIdleAudioSuspend = (howler: HowlerLike | null | undefined): void => {
  if (!howler) return;
  howler.autoSuspend = false;
};

export const installAudioResumeHandlers = (howler: HowlerLike): (() => void) => {
  const resume = () => resumeAudioContext(howler);

  // A trip to the background is what can leave the context lying about its
  // state, so it earns a full restart: once on the first foreground signal (the
  // several that fire together share it), and once more on the first tap, where
  // iOS cannot refuse it for want of a gesture. Every other signal keeps the
  // cheap resume. Web sessions without a hide signal retain the old behaviour.
  // Native listener installation is asynchronous. Arm one conservative recovery
  // during registration so a missed initial inactive event cannot strand audio.
  const nativeRegistration = Capacitor.isNativePlatform();
  let restartOnForeground = nativeRegistration;
  let restartOnGesture = nativeRegistration;
  let backgroundVersion = 0;
  let foreground = false;
  let waitingForRestart: Promise<ResumeOutcome> | undefined;
  const onHidden = () => {
    foreground = false;
    backgroundVersion += 1;
    restartOnForeground = true;
    restartOnGesture = true;
  };
  let restartVersion = 0;
  const restart = (replacePending = false) => {
    const version = backgroundVersion;
    const attempt = ++restartVersion;
    void restartAudioContext(howler, { replacePending }).then((outcome) => {
      if (tornDown || version !== backgroundVersion || attempt !== restartVersion) return;
      if (outcome === 'failed' || outcome === 'unavailable') {
        restartOnForeground = true;
        restartOnGesture = true;
      }
    });
  };
  const onForeground = (): void => {
    foreground = true;
    const pending = restarts.get(howler);
    if (pending) {
      // A newer background trip still needs its own recovery. Replay its
      // foreground once the current cycle settles, without another UI event.
      // Duplicate signals share one waiter; ordinary failed cycles still wait
      // for the gesture fallback rather than retrying automatically in a loop.
      if (restartOnForeground && waitingForRestart !== pending) {
        waitingForRestart = pending;
        void pending.then(() => {
          if (waitingForRestart === pending) waitingForRestart = undefined;
          if (!tornDown && foreground && restartOnForeground) onForeground();
        });
      }
      return;
    }
    if (!restartOnForeground) {
      resume();
      return;
    }
    restartOnForeground = false;
    restart();
  };
  const onGesture = () => {
    // Use the first valid activation immediately, even if the foreground
    // attempt is pending or blocked by autoplay policy. Native calls must be
    // queued here, not in the older attempt's asynchronous completion.
    if (!restartOnGesture && !needsGestureRecovery.has(howler)) {
      resume();
      return;
    }
    // Covers a foreground signal that never arrived, too.
    restartOnForeground = false;
    restartOnGesture = false;
    restart(true);
  };

  const onVisibility = () => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') onHidden();
    else if (document.visibilityState === 'visible') onForeground();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onForeground);
  window.addEventListener('pagehide', onHidden);
  window.addEventListener('pageshow', onForeground);

  // Gesture fallback, in the CAPTURE phase on `document`.
  //
  // iOS/WKWebView refuses `resume()` outside a user gesture, so this is the path
  // that actually recovers audio after the OS suspends the context — the
  // foreground listeners above get rejected. It has to be capture-phase because
  // the table surface has drag/swipe handlers that call stopPropagation, which
  // silently prevented the earlier bubble-phase window listeners from ever
  // seeing a tap on the felt. Passive, and a no-op once the context is running.
  //
  // Only touchend/click/keydown take the post-background restart: WebKit does
  // not count touchstart or pointerdown as a user activation, so a restart spent
  // there could be refused and leave the real tap nothing to do.
  const gestureOpts = { passive: true, capture: true } as const;
  document.addEventListener('pointerdown', resume, gestureOpts);
  document.addEventListener('touchstart', resume, gestureOpts);
  document.addEventListener('touchend', onGesture, gestureOpts);
  document.addEventListener('click', onGesture, gestureOpts);
  document.addEventListener('keydown', onGesture, gestureOpts);

  let removeCapacitorListener: (() => void) | undefined;
  // The native listener lands asynchronously; a teardown that beats it must
  // still remove it, or it outlives the effect that installed it.
  let tornDown = false;
  (async () => {
    try {
      const { App } = await import('@capacitor/app');
      let nativeStateVersion = 0;
      const handle = await App.addListener('appStateChange', (state: { isActive: boolean }) => {
        nativeStateVersion += 1;
        if (state.isActive) onForeground();
        else onHidden();
      });
      removeCapacitorListener = () => handle.remove();
      if (tornDown) {
        removeCapacitorListener();
        return;
      }
      if (nativeRegistration) {
        const version = nativeStateVersion;
        const state = await App.getState();
        if (tornDown || version !== nativeStateVersion) return;
        if (state.isActive) onForeground();
        else onHidden();
      }
    } catch {
      /* not on Capacitor — the web listeners above cover it */
    }
  })();

  return () => {
    tornDown = true;
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onForeground);
    window.removeEventListener('pagehide', onHidden);
    window.removeEventListener('pageshow', onForeground);
    document.removeEventListener('pointerdown', resume, gestureOpts);
    document.removeEventListener('touchstart', resume, gestureOpts);
    document.removeEventListener('touchend', onGesture, gestureOpts);
    document.removeEventListener('click', onGesture, gestureOpts);
    document.removeEventListener('keydown', onGesture, gestureOpts);
    removeCapacitorListener?.();
  };
};
